import { useState, useEffect, useRef, useCallback } from 'react';
import {
  fetchAuthProfile,
  fetchDemoScenarios,
  requestMockAuthChallenge,
  verifyMockAuthCode,
} from '../lib/api';
import { getAnonymousUserId } from '../lib/user-context';
import {
  MOCK_AUTH_SUCCESS_CODE,
  MOCK_AUTH_FAILURE_CODE,
  readSessionCookie,
  loadStoredMockAuth,
  persistStoredMockAuth,
  clearStoredMockAuth,
  normalizeMockAuthCodeInput,
  isValidMockAuthEmail,
  isDisallowedMockAuthEmailDomain,
  buildGenericAuthPrompt,
} from '../components/askbox-utils';

/**
 * useMockAuth — encapsulates all mock-authentication state, computed values,
 * and callbacks that were previously inlined in AskBox.
 *
 * The hook does NOT own conversation state.  Two callbacks are accepted so the
 * hook can request side-effects that live in the parent:
 *   - onConversationClearAuthPrompt()  — clear the auth-prompt from conversation messages
 *   - onClearConversation()            — full conversation reset (logout)
 *   - onSubmitAfterAuth(opts)          — re-submit the pending question after verification
 */
export function useMockAuth(
  clientId,
  clientConfig,
  {
    initialPreferredScenarioCode = null,
    hasSeededLaunch = false,
    initialQuery = '',
    initialAnswerId = null,
    conversationStarted = false,
    onConversationClearAuthPrompt = null,
    onClearConversation = null,
    onSubmitAfterAuth = null,
  } = {},
) {
  // ── state ──────────────────────────────────────────────────────────
  const [mockAuthSession, setMockAuthSession] = useState(() => loadStoredMockAuth());
  const [authProfile, setAuthProfile] = useState(null);
  const [authPrompt, setAuthPrompt] = useState(null);
  const [authMessageIndex, setAuthMessageIndex] = useState(null);
  const [authFormOpen, setAuthFormOpen] = useState(false);
  const [footerAuthOpen, setFooterAuthOpen] = useState(false);
  const [authStep, setAuthStep] = useState('code');
  const [authEmail, setAuthEmail] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [authManualCodeOpen, setAuthManualCodeOpen] = useState(false);
  const [authScenarioOptions, setAuthScenarioOptions] = useState([]);
  const [demoScenarioOptions, setDemoScenarioOptions] = useState([]);
  const [preferredScenarioCode, setPreferredScenarioCode] = useState(initialPreferredScenarioCode);
  const [isSeededLaunchPending, setIsSeededLaunchPending] = useState(hasSeededLaunch);
  const [authError, setAuthError] = useState('');
  const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);
  const [authChallengeSessionId, setAuthChallengeSessionId] = useState(null);
  const [authSuccessPulse, setAuthSuccessPulse] = useState(false);

  // ── refs ────────────────────────────────────────────────────────────
  const autoScenarioSigninKeyRef = useRef(null);
  const pendingAuthResumeRef = useRef(null);
  // Tracks the auth session ID we *just* wrote a profile for from a verify
  // response. The loadProfile effect consumes this on its next run to skip a
  // redundant /api/auth/profile round-trip. Without this, the just-set profile
  // can get clobbered (and localStorage wiped) when the GET hits a different
  // backend worker than the verify did and returns {authenticated: false}.
  const freshProfileSessionRef = useRef(null);

  // ── derived / computed ─────────────────────────────────────────────
  const isDemoMode = Boolean(
    clientConfig?.liveDemoPage?.enabled ||
    (clientConfig?.drawer?.enabled && clientConfig?.drawer?.mode === 'demo-info'),
  );

  const cookieSession = readSessionCookie().session;
  const cookieAuthSessionId = cookieSession?.session_id || null;
  const storedMockAuthSessionId = mockAuthSession?.authSessionId || null;
  const profileLookupAuthSessionId = cookieAuthSessionId || storedMockAuthSessionId || null;

  const hasValidatedMockAuth = Boolean(
    !cookieAuthSessionId && storedMockAuthSessionId && authProfile?.authenticated,
  );

  const effectiveAuthSessionId =
    cookieAuthSessionId || (hasValidatedMockAuth ? storedMockAuthSessionId : null);

  const fallbackMockScenario = authProfile?.profile?.mock_profile_label
    ? {
        label: authProfile.profile.mock_profile_label,
        code: authProfile?.profile?.mock_profile_code || null,
      }
    : null;

  const effectiveAuthScenario = hasValidatedMockAuth
    ? mockAuthSession?.scenario || fallbackMockScenario
    : fallbackMockScenario;

  const effectiveAuthScenarioLabel = effectiveAuthScenario?.label || null;

  const effectiveAuthDisplayName =
    authProfile?.profile?.person?.full_name || authProfile?.profile?.user_name || null;

  const effectiveAuthEmail = hasValidatedMockAuth
    ? mockAuthSession?.email ||
      authProfile?.profile?.person?.email ||
      authProfile?.profile?.email ||
      null
    : authProfile?.profile?.person?.email || authProfile?.profile?.email || null;

  const effectiveAuthIdentityPrimary = effectiveAuthDisplayName || effectiveAuthEmail || 'member';

  const effectiveAuthIdentitySecondary =
    effectiveAuthDisplayName &&
    effectiveAuthEmail &&
    effectiveAuthDisplayName.trim().toLowerCase() !== effectiveAuthEmail.trim().toLowerCase()
      ? effectiveAuthEmail
      : null;

  const authSessionTtlDays = Number.isFinite(Number(clientConfig?.authSessionTtlDays))
    ? Number(clientConfig.authSessionTtlDays)
    : 60;

  const rememberLogin = clientConfig?.rememberLogin !== false;

  const shouldAutoSwitchScenario = Boolean(
    isDemoMode &&
    preferredScenarioCode &&
    !cookieAuthSessionId &&
    effectiveAuthScenario?.code !== preferredScenarioCode,
  );

  // ── effects ────────────────────────────────────────────────────────

  // Session expiration check
  useEffect(() => {
    if (!mockAuthSession) return;

    if (!rememberLogin) {
      clearStoredMockAuth();
      if (!mockAuthSession.sessionOnly) {
        setMockAuthSession(null);
      }
      return;
    }

    const expiresAt = mockAuthSession.expiresAt ? Date.parse(mockAuthSession.expiresAt) : NaN;
    if (Number.isFinite(expiresAt) && expiresAt <= Date.now()) {
      clearStoredMockAuth();
      setMockAuthSession(null);
      setAuthProfile(null);
      setAuthPrompt(null);
      setAuthFormOpen(false);
    }
  }, [mockAuthSession, rememberLogin]);

  // Auth success pulse animation timer
  useEffect(() => {
    if (!authSuccessPulse) return undefined;

    const timeoutId = window.setTimeout(() => {
      setAuthSuccessPulse(false);
    }, 1500);

    return () => window.clearTimeout(timeoutId);
  }, [authSuccessPulse]);

  // Load auth profile
  useEffect(() => {
    // If we just wrote a profile for this session from a verify response,
    // skip the redundant validation round-trip. The verify response is
    // authoritative; re-fetching can race with a different backend worker
    // that doesn't have the session and wipe localStorage.
    if (
      profileLookupAuthSessionId &&
      freshProfileSessionRef.current === profileLookupAuthSessionId
    ) {
      freshProfileSessionRef.current = null;
      return undefined;
    }

    let cancelled = false;

    async function loadProfile() {
      if (!profileLookupAuthSessionId) {
        setAuthProfile(null);
        return;
      }

      try {
        const profile = await fetchAuthProfile(profileLookupAuthSessionId, clientId);
        if (!cancelled) {
          if (profile?.authenticated) {
            setAuthProfile(profile);
          } else {
            if (!cookieAuthSessionId) {
              clearStoredMockAuth();
              setMockAuthSession(null);
            }
            setAuthProfile(null);
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load auth profile:', error);
        if (!cookieAuthSessionId) {
          clearStoredMockAuth();
          setMockAuthSession(null);
        }
        setAuthProfile(null);
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [profileLookupAuthSessionId, clientId, cookieAuthSessionId]);

  // Load demo scenarios
  useEffect(() => {
    let cancelled = false;

    async function loadDemoScenarios() {
      if (!isDemoMode) {
        setDemoScenarioOptions([]);
        return;
      }

      try {
        const result = await fetchDemoScenarios(clientId);
        if (!cancelled) {
          setDemoScenarioOptions(Array.isArray(result?.scenarios) ? result.scenarios : []);
        }
      } catch (_error) {
        if (!cancelled) {
          setDemoScenarioOptions([]);
        }
      }
    }

    loadDemoScenarios();
    return () => {
      cancelled = true;
    };
  }, [clientId, isDemoMode]);

  // Sync preferredScenarioCode from props
  useEffect(() => {
    setPreferredScenarioCode(initialPreferredScenarioCode || null);
  }, [initialPreferredScenarioCode]);

  // Sync isSeededLaunchPending from props
  useEffect(() => {
    setIsSeededLaunchPending(hasSeededLaunch);
  }, [hasSeededLaunch]);

  // Clear seeded launch pending once conversation starts
  useEffect(() => {
    if (conversationStarted) {
      setIsSeededLaunchPending(false);
    }
  }, [conversationStarted]);

  // ── callbacks ──────────────────────────────────────────────────────

  const resetAuthToEmail = useCallback(() => {
    setAuthStep('code');
    setAuthCode('');
    setAuthError('');
    setAuthManualCodeOpen(false);
  }, []);

  const buildHiddenMockAuthEmail = useCallback(
    (code = null) => {
      const normalizedCode = normalizeMockAuthCodeInput(code || '');
      if (
        effectiveAuthEmail &&
        isValidMockAuthEmail(effectiveAuthEmail) &&
        !isDisallowedMockAuthEmailDomain(effectiveAuthEmail)
      ) {
        return effectiveAuthEmail.trim();
      }
      return `member+${clientId}${normalizedCode ? `-${normalizedCode}` : ''}@example.org`;
    },
    [clientId, effectiveAuthEmail],
  );

  const requestAuthChallenge = useCallback(
    async (email, sessionId = null) => {
      const challengeSession =
        sessionId || authChallengeSessionId || `auth-${Date.now().toString(36)}`;
      setAuthChallengeSessionId(challengeSession);
      const challenge = await requestMockAuthChallenge(clientId, challengeSession, email);
      const scenarios = Array.isArray(challenge.available_scenarios)
        ? challenge.available_scenarios
        : [];
      const resolvedScenarios = scenarios.length > 0 ? scenarios : demoScenarioOptions;
      setAuthScenarioOptions(resolvedScenarios);
      setAuthCode('');
      setAuthManualCodeOpen(false);
      setAuthStep('code');
      return { challenge, challengeSession, scenarios: resolvedScenarios };
    },
    [clientId, authChallengeSessionId, demoScenarioOptions],
  );

  const ensureMockAuthChallenge = useCallback(
    async (code, emailOverride = null, sessionId = null) => {
      const email = (emailOverride || authEmail.trim() || buildHiddenMockAuthEmail(code)).trim();
      setAuthEmail(email);
      return requestAuthChallenge(email, sessionId);
    },
    [authEmail, buildHiddenMockAuthEmail, requestAuthChallenge],
  );

  const completeMockAuthVerification = useCallback(
    async (code, { emailOverride = null, sessionId = null } = {}) => {
      const normalizedCode = normalizeMockAuthCodeInput(code);
      if (!normalizedCode) {
        throw new Error('Enter the verification code first.');
      }

      const { challengeSession } = await ensureMockAuthChallenge(
        normalizedCode,
        emailOverride,
        sessionId,
      );
      const result = await verifyMockAuthCode(
        clientId,
        challengeSession,
        normalizedCode,
        getAnonymousUserId(),
      );
      const now = new Date();
      const expiresAt = rememberLogin
        ? new Date(
            now.getTime() + Math.max(authSessionTtlDays, 1) * 24 * 60 * 60 * 1000,
          ).toISOString()
        : null;
      const nextMockSession = {
        authSessionId: result.auth_session_id,
        email:
          result.member_profile?.person?.email ||
          authEmail.trim() ||
          buildHiddenMockAuthEmail(normalizedCode),
        scenario: result.selected_scenario || null,
        issuedAt: now.toISOString(),
        expiresAt,
        sessionOnly: !rememberLogin,
      };

      if (rememberLogin) {
        persistStoredMockAuth(nextMockSession);
      } else {
        clearStoredMockAuth();
      }

      // Mark this session as freshly verified so the loadProfile effect skips
      // the redundant /api/auth/profile round-trip on the state change below.
      freshProfileSessionRef.current = result.auth_session_id;
      setMockAuthSession(nextMockSession);
      setAuthSuccessPulse(true);
      setAuthProfile({
        authenticated: true,
        profile: result.member_profile,
        source: 'mock',
      });

      // Clear auth prompt from conversation messages via parent callback
      if (onConversationClearAuthPrompt) {
        onConversationClearAuthPrompt();
      }

      setAuthPrompt(null);
      setAuthMessageIndex(null);
      setAuthFormOpen(false);
      setFooterAuthOpen(false);
      resetAuthToEmail();
      setAuthChallengeSessionId(challengeSession);

      const pending = pendingAuthResumeRef.current;
      pendingAuthResumeRef.current = null;
      if (pending && onSubmitAfterAuth) {
        await onSubmitAfterAuth({
          question: pending.question,
          source: pending.source,
          displayMessage: pending.displayMessage,
          forcedAuthSessionId: result.auth_session_id,
        });
      }

      return result;
    },
    [
      clientId,
      authEmail,
      authSessionTtlDays,
      rememberLogin,
      buildHiddenMockAuthEmail,
      ensureMockAuthChallenge,
      resetAuthToEmail,
      onConversationClearAuthPrompt,
      onSubmitAfterAuth,
    ],
  );

  const openAuthForm = useCallback(() => {
    setFooterAuthOpen(true);
    setPreferredScenarioCode(null);
    resetAuthToEmail();
    setAuthScenarioOptions(demoScenarioOptions);
  }, [resetAuthToEmail, demoScenarioOptions]);

  const openInlineAuthForm = useCallback(() => {
    if (!authPrompt) return;
    setAuthFormOpen(true);
    setPreferredScenarioCode(null);
    resetAuthToEmail();
    setAuthScenarioOptions(demoScenarioOptions);
  }, [authPrompt, resetAuthToEmail, demoScenarioOptions]);

  const handleAuthStart = useCallback(
    async (e) => {
      e?.preventDefault();
      const email = authEmail.trim();
      if (!email) {
        setAuthError('Enter an email address first.');
        return;
      }
      if (!isValidMockAuthEmail(email)) {
        setAuthError('Enter a valid email address.');
        return;
      }
      if (isDisallowedMockAuthEmailDomain(email)) {
        setAuthError('Disposable email addresses are not allowed.');
        return;
      }

      setIsAuthSubmitting(true);
      setAuthError('');

      try {
        await requestAuthChallenge(email);
      } catch (error) {
        setAuthError(error.message || 'Could not send a verification code.');
      } finally {
        setIsAuthSubmitting(false);
      }
    },
    [authEmail, requestAuthChallenge],
  );

  const handleAuthVerify = useCallback(
    async (e, overrideCode = null) => {
      e?.preventDefault();
      const code = normalizeMockAuthCodeInput(overrideCode || authCode);
      if (!code) {
        setAuthError('Enter the verification code first.');
        return;
      }

      setIsAuthSubmitting(true);
      setAuthError('');
      setAuthCode(code);

      try {
        await completeMockAuthVerification(code);
      } catch (error) {
        setAuthError(error.message || 'That code did not verify.');
      } finally {
        setIsAuthSubmitting(false);
      }
    },
    [authCode, completeMockAuthVerification],
  );

  const handleAuthSelectScenario = useCallback(
    async (scenarioCode) => {
      setAuthCode(normalizeMockAuthCodeInput(scenarioCode));
      await handleAuthVerify(null, scenarioCode);
    },
    [handleAuthVerify],
  );

  const handleStartDrawerScenario = useCallback(
    async (scenarioCode) => {
      setPreferredScenarioCode(scenarioCode);
      setAuthScenarioOptions(demoScenarioOptions);
      setIsAuthSubmitting(true);

      try {
        await completeMockAuthVerification(scenarioCode, {
          emailOverride: buildHiddenMockAuthEmail(scenarioCode),
        });
      } catch (error) {
        setFooterAuthOpen(true);
        setAuthCode(normalizeMockAuthCodeInput(scenarioCode));
        setAuthError(error.message || 'Could not sign in with that demo member.');
      } finally {
        setIsAuthSubmitting(false);
      }
    },
    [demoScenarioOptions, completeMockAuthVerification, buildHiddenMockAuthEmail],
  );

  const handleAuthLogout = useCallback(() => {
    if (onClearConversation) {
      onClearConversation();
    }
    clearStoredMockAuth();
    setMockAuthSession(null);
    setAuthProfile(null);
    setAuthPrompt(null);
    setAuthMessageIndex(null);
    setAuthFormOpen(false);
    setFooterAuthOpen(false);
    setAuthStep('code');
    setAuthEmail('');
    setAuthCode('');
    setPreferredScenarioCode(null);
    setAuthManualCodeOpen(false);
    setAuthScenarioOptions([]);
    setAuthError('');
    setAuthChallengeSessionId(null);
    setAuthSuccessPulse(false);
    pendingAuthResumeRef.current = null;
    freshProfileSessionRef.current = null;
  }, [onClearConversation]);

  // Auto-switch demo scenario
  useEffect(() => {
    if (!shouldAutoSwitchScenario) {
      autoScenarioSigninKeyRef.current = null;
      return undefined;
    }

    const attemptKey = `${preferredScenarioCode}:${initialQuery || ''}:${initialAnswerId || ''}`;
    if (autoScenarioSigninKeyRef.current === attemptKey) {
      return undefined;
    }
    autoScenarioSigninKeyRef.current = attemptKey;

    let cancelled = false;

    const run = async () => {
      setIsAuthSubmitting(true);
      setAuthError('');
      try {
        await completeMockAuthVerification(preferredScenarioCode, {
          emailOverride: buildHiddenMockAuthEmail(preferredScenarioCode),
        });
      } catch (error) {
        if (cancelled) return;
        setFooterAuthOpen(true);
        setAuthCode(normalizeMockAuthCodeInput(preferredScenarioCode));
        setAuthError(error.message || 'Could not sign in with that demo member.');
      } finally {
        // Always clear the busy flag, even if this effect run was cancelled.
        // The verify state-writes inside completeMockAuthVerification reliably
        // re-trigger this effect mid-flight (callback identities change), so
        // guarding the reset on `!cancelled` left the button stuck on "Working…".
        setIsAuthSubmitting(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    buildHiddenMockAuthEmail,
    completeMockAuthVerification,
    initialAnswerId,
    initialQuery,
    preferredScenarioCode,
    shouldAutoSwitchScenario,
  ]);

  // ── helpers exposed for handleSubmit in AskBox ─────────────────────

  /**
   * Called by AskBox when a 'complete' SSE event carries an auth_prompt.
   * Stores the pending question so it can be re-submitted after verification.
   */
  const setPendingAuthResume = useCallback((pending) => {
    pendingAuthResumeRef.current = pending;
  }, []);

  /**
   * Called by AskBox's handleSubmit to resolve the auth_session_id to send
   * in the request payload.  Reads the cookie fresh each call.
   */
  const resolveAuthSessionId = useCallback(
    (forcedId = null) => {
      if (forcedId) return forcedId;
      const { session } = readSessionCookie();
      return session?.session_id || mockAuthSession?.authSessionId || null;
    },
    [mockAuthSession],
  );

  // ── return ─────────────────────────────────────────────────────────
  return {
    // state
    mockAuthSession,
    authProfile,
    authPrompt,
    authMessageIndex,
    authFormOpen,
    footerAuthOpen,
    authStep,
    authEmail,
    authCode,
    authManualCodeOpen,
    authScenarioOptions,
    demoScenarioOptions,
    preferredScenarioCode,
    isSeededLaunchPending,
    authError,
    isAuthSubmitting,
    authChallengeSessionId,
    authSuccessPulse,

    // computed
    cookieAuthSessionId,
    effectiveAuthSessionId,
    effectiveAuthIdentityPrimary,
    effectiveAuthIdentitySecondary,
    effectiveAuthEmail,
    effectiveAuthScenario,
    effectiveAuthScenarioLabel,
    effectiveAuthDisplayName,
    hasValidatedMockAuth,
    isDemoMode,
    authSessionTtlDays,
    rememberLogin,
    shouldAutoSwitchScenario,

    // setters (used by AskBox for direct state updates)
    setAuthPrompt,
    setAuthMessageIndex,
    setAuthFormOpen,
    setFooterAuthOpen,
    setAuthStep,
    setAuthEmail,
    setAuthCode,
    setAuthManualCodeOpen,
    setAuthError,
    setAuthChallengeSessionId,
    setIsSeededLaunchPending,

    // callbacks
    resetAuthToEmail,
    buildHiddenMockAuthEmail,
    requestAuthChallenge,
    ensureMockAuthChallenge,
    completeMockAuthVerification,
    openAuthForm,
    openInlineAuthForm,
    handleAuthStart,
    handleAuthVerify,
    handleAuthSelectScenario,
    handleStartDrawerScenario,
    handleAuthLogout,
    setPendingAuthResume,
    resolveAuthSessionId,
    buildGenericAuthPrompt,
  };
}
